import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputFile } from '@/components/forms/form-input-file.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { bulkUpdateStudentLearningPathByCsv, getStudents, getStudentsOfSchool } from '@/lib/api-calls.js';
import { BULK_UPDATE_LEARNING_PATH_CSV_TEMPLATE_URL, csvFormSchema, parseCsvAndUpload } from '@/lib/csv-utils.js';
import { pluralize } from '@/lib/string-utils.js';
import { BulkUpdateStudentLearningPathCsvFormKeys, bulkUpdateStudentLearningPathCsvRow } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface BulkUpdateLearningPathByCsvProps {
  trigger: ReactNode;
  onSuccess?: () => void;
  /** Scopes the update to that school's roster and refreshes it instead of the all-students list. */
  schoolId?: string;
}

const EMPTY_ERRORS: string[] = [];
const formSchema = csvFormSchema;

export const BulkUpdateLearningPathByCsv = ({ trigger, onSuccess, schoolId }: BulkUpdateLearningPathByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const { mutateAsync } = useMutation({
    mutationFn: bulkUpdateStudentLearningPathByCsv,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile } = formData;

    let result;
    try {
      result = await parseCsvAndUpload({
        file: csvFile[0],
        rowSchema: bulkUpdateStudentLearningPathCsvRow,
        onUpload: async (file) => {
          const uploadForm = new FormData();
          uploadForm.append(BulkUpdateStudentLearningPathCsvFormKeys.csvFile, file);
          if (schoolId) {
            uploadForm.append(BulkUpdateStudentLearningPathCsvFormKeys.schoolId, schoolId);
          }
          return mutateAsync(uploadForm);
        },
        onToast: toast,
        onSetErrors: setErrorMessages,
      });
    } catch (error) {
      // A NotFoundError means the picked file changed on disk after selection.
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        setErrorMessages(['The selected file could not be read. Choose the CSV again and retry.']);
      }
      return 0;
    }

    const { successRowsCount, updatedRowsCount, hasErrors } = result;

    if (successRowsCount > 0) {
      const count = updatedRowsCount > 0 ? updatedRowsCount : successRowsCount;
      toast({
        title: `${count} ${pluralize(count, 'student', 'students')} updated`,
      });
      onSuccess?.();
      if (schoolId) {
        queryClient.invalidateQueries(getStudentsOfSchool(schoolId));
      } else {
        queryClient.invalidateQueries(getStudents());
      }
    }

    if (!hasErrors) {
      closeModal();
    }

    return successRowsCount;
  };

  return (
    <FormModal
      modalTitle="Bulk update learning path & starting lesson"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Add"
      trigger={trigger}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {({ form }) => (
        <>
          <div className="grid grid-cols-1 gap-2 py-4">
            <FormInputFile
              form={form}
              name="csvFile"
              label="Students CSV"
              inputProps={{
                accept: '.csv',
                className: 'w-full',
              }}
            />
            <span className="text-sm text-gray-500">
              Each row must identify a student by Email, FLEID, or student ID. If multiple identifiers are included,
              they must all identify the same student. See{' '}
              <a
                href={BULK_UPDATE_LEARNING_PATH_CSV_TEMPLATE_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-600 hover:underline"
              >
                template (to update)
              </a>{' '}
              for CSV format. Export the sheet as csv file
            </span>
          </div>
          {errorMessages.length > 0 && (
            <div className="w-full mb-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error while parsing CSV</AlertTitle>
                <AlertDescription className="max-h-[240px] overflow-y-auto">
                  {errorMessages.map((message, index) => (
                    <p key={index}>{message}</p>
                  ))}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </>
      )}
    </FormModal>
  );
};
